import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { filterCompanyEntries } from '@/lib/company-switcher';
import { cn } from '@/lib/utils';
import type { MyCompanyResponse } from '@/types';
import { Building2, Plus, Search } from 'lucide-react';
import { useState } from 'react';

interface CompanySwitcherPanelProps {
  companies: readonly MyCompanyResponse[];
  activeCompanyId?: string | null;
  onSelectCompany: (company: MyCompanyResponse['company']) => void;
  onCreateCompany: () => void;
  createLabel?: string;
  emptyLabel?: string;
  className?: string;
}

export function CompanySwitcherPanel({
  companies,
  activeCompanyId,
  onSelectCompany,
  onCreateCompany,
  createLabel = 'Adicionar nova empresa',
  emptyLabel = 'Nenhuma empresa encontrada. Tente outro nome ou CNPJ.',
  className,
}: CompanySwitcherPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredCompanies = filterCompanyEntries(companies, searchTerm);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="space-y-2">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar por nome da empresa ou CNPJ"
            className="h-10 pl-9"
          />
        </div>
        <p className="text-muted-foreground text-xs">
          {filteredCompanies.length} {filteredCompanies.length === 1 ? 'empresa encontrada' : 'empresas encontradas'}
        </p>
      </div>

      <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
        {filteredCompanies.length > 0 ? (
          filteredCompanies.map((entry) => (
            <button
              key={entry.company.id}
              type="button"
              onClick={() => onSelectCompany(entry.company)}
              className={cn(
                'hover:bg-sidebar-muted/85 border-sidebar-border/60 text-foreground flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm transition-colors',
                activeCompanyId === entry.company.id && 'bg-sidebar-muted font-semibold shadow-sm',
              )}
            >
              <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl">
                {entry.company.profilePhoto ? (
                  <img
                    src={entry.company.profilePhoto}
                    alt={entry.company.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Building2 className="text-primary h-4 w-4" />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate font-medium">{entry.company.name}</p>
                <p className="text-muted-foreground text-xs">{entry.company.registerNumber}</p>
              </div>
            </button>
          ))
        ) : (
          <div className="text-muted-foreground rounded-2xl border border-dashed px-4 py-5 text-sm">{emptyLabel}</div>
        )}
      </div>

      <Button type="button" variant="outline" className="w-full justify-start gap-2" onClick={onCreateCompany}>
        <Plus className="h-4 w-4" />
        {createLabel}
      </Button>
    </div>
  );
}
